import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * RouteAnnouncer — Enterprise Design System
 *
 * An aria-live region that announces page changes to screen readers on route
 * navigation. Announces the new page title within 500ms of route change.
 *
 * Requirements: 17.8
 */

export default function RouteAnnouncer() {
  const location = useLocation();
  const [announcement, setAnnouncement] = useState('');
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Clear any pending announcement
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Announce within 500ms of route change
    timeoutRef.current = setTimeout(() => {
      const title = document.title || 'Page loaded';
      setAnnouncement(`Navigated to ${title}`);
    }, 100);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [location.pathname]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    >
      {announcement}
    </div>
  );
}
